/**
 * 通用工具函数
 */

/**
 * 生成 UUID v4 格式的唯一 ID
 * 兼容数据库 UUID 类型
 */
export function generateId(): string {
  // 使用 crypto.randomUUID() 如果可用，否则使用 polyfill
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Polyfill for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 限制数值在指定范围内
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * 复制文本到剪贴板（Clipboard API + execCommand fallback）
 * Clipboard API 在非 HTTPS、document 未 focus、Safari 严格 user-activation 等场景会失败，
 * 此时 fallback 到传统 textarea + execCommand('copy') 方案。
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // 1. 优先尝试 Clipboard API
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy approach
    }
  }
  // 2. Fallback: textarea + execCommand
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // 隐藏但保持在 viewport 内（某些浏览器不复制不可见元素）
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * 深度比较两个对象是否相等
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (typeof a !== 'object') return a === b;
  
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  
  if (aKeys.length !== bKeys.length) return false;
  
  return aKeys.every(key => deepEqual(aObj[key], bObj[key]));
}
